'use client';

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

/**
 * ThemeContext - Gestiona el tema de la aplicación (light/dark/system)
 * 
 * Características:
 * - Persistencia en localStorage
 * - Soporte para preferencia del sistema
 * - Evita flash aplicando tema antes del render
 */

export type Theme = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

interface ThemeContextType {
    theme: Theme;
    resolvedTheme: ResolvedTheme;
    setTheme: (theme: Theme) => void;
    isDark: boolean;
    isLight: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: ReactNode }) {
    const [theme, setThemeState] = useState<Theme>('system');
    // 'light' por defecto para coincidir con el script inline de layout.tsx.
    // Si difieren, el primer render pinta un tema y el efecto lo cambia por otro.
    const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>('light');

    // Aplicar tema al elemento HTML
    const applyTheme = (isDark: boolean) => {
        if (isDark) {
            document.documentElement.classList.add('dark');
        } else {
            document.documentElement.classList.remove('dark');
        }
        setResolvedTheme(isDark ? 'dark' : 'light');
    };

    // Resolver tema basado en preferencia
    const resolveTheme = (themeValue: Theme): boolean => {
        if (themeValue === 'system') {
            return window.matchMedia('(prefers-color-scheme: dark)').matches;
        }
        return themeValue === 'dark';
    };

    // Cargar tema inicial desde localStorage
    useEffect(() => {
        const stored = localStorage.getItem('theme') as Theme | null;
        const initial = stored || 'light';
        setThemeState(initial);
        applyTheme(resolveTheme(initial));
    }, []);

    // Escuchar cambios en preferencia del sistema
    useEffect(() => {
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

        const handleChange = (e: MediaQueryListEvent) => {
            if (theme === 'system') {
                applyTheme(e.matches);
            }
        };

        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
    }, [theme]);

    // Función para cambiar el tema
    const setTheme = (newTheme: Theme) => {
        setThemeState(newTheme);
        localStorage.setItem('theme', newTheme);
        applyTheme(resolveTheme(newTheme));
    };

    const value: ThemeContextType = {
        theme,
        resolvedTheme,
        setTheme,
        isDark: resolvedTheme === 'dark',
        isLight: resolvedTheme === 'light',
    };

    return (
        <ThemeContext.Provider value={value}>
            {children}
        </ThemeContext.Provider>
    );
}

export function useTheme() {
    const context = useContext(ThemeContext);
    if (context === undefined) {
        throw new Error('useTheme debe usarse dentro de ThemeProvider');
    }
    return context;
}
